// Wall/floor/ceiling attachment orientation (MB.1). PURE (ENG-1).
//
// Small redstone components (torches, and later levers/buttons) mount on whichever
// face of a block the player clicked: the top → floor, a side → wall (pointing
// outward, away from the support), the bottom → ceiling. This resolves the clicked
// face normal into the attachment + the horizontal facing the client meshes and
// the redstone engine reads. The glow half already landed (game/glow); this is the
// facing/placement half. Wall torches are separate blocks (wall_torch /
// redstone_wall_torch), so no existing block-state ids shift.

export type Facing = 'north' | 'east' | 'south' | 'west';
export type Attach = 'floor' | 'wall' | 'ceiling';

/** The outward facing for a horizontal click normal, or null for top/bottom. */
export function wallTorchFacing(nx: number, ny: number, nz: number): Facing | null {
  if (ny !== 0) return null;
  if (nx === 1) return 'east';
  if (nx === -1) return 'west';
  if (nz === 1) return 'south';
  if (nz === -1) return 'north';
  return null;
}

export interface Attachment {
  face: Attach;
  /** Horizontal facing; for floor/ceiling it comes from the player's look. */
  facing: Facing | null;
}

/**
 * Resolve a clicked-face normal (+ the player's horizontal facing for floor/ceiling
 * mounts) into an attachment. A side click walls the component facing outward
 * (= the normal); a top click floors it, a bottom click ceilings it, both facing
 * the player.
 */
export function attachmentFor(nx: number, ny: number, nz: number, playerFacing: Facing = 'north'): Attachment {
  if (ny > 0) return { face: 'floor', facing: playerFacing };
  if (ny < 0) return { face: 'ceiling', facing: playerFacing };
  return { face: 'wall', facing: wallTorchFacing(nx, ny, nz) };
}

/** The support side a wall-mounted component attaches to (opposite its facing). */
export function wallSupport(facing: Facing): Facing {
  return facing === 'north' ? 'south' : facing === 'south' ? 'north' : facing === 'east' ? 'west' : 'east';
}

/**
 * The direction a face-mounted lever/button strong-powers — the block it is
 * attached to (MB.1 wall-mount). A floor mount powers the block below, a ceiling
 * mount the block above, a wall mount the support behind it (`wallSupport`).
 * Absent props (a legacy floor lever) → `down`, so existing placements are
 * unchanged.
 */
export function attachSupport(face: string | undefined, facing: string | undefined): 'up' | 'down' | Facing {
  if (face === 'ceiling') return 'up';
  if (face === 'wall') return wallSupport((facing ?? 'north') as Facing);
  return 'down'; // floor / absent
}
